// Axios-specific cancellation type, thrown when a request's signal is aborted. An axios major
// upgrade that renames or reshapes it has to be reflected in the catches below, or every
// superseded request starts dispatching an error - see tests/services/hardwareInventory.
import { CanceledError } from 'axios';
import { GET_COMPLIANCE_VERDICTS_DEFAULT_QUERY_PARAMS, OVERRIDE_JOB_POLL } from '../../constants/compliance-verdicts';
import { getApiErrorMessage } from '../../helpers/apiError';
import * as complianceVerdictsService from '../../services/complianceVerdicts';
import { describeJobOutcome, isJobSucceeded, pollJobUntilTerminal } from '../../services/jobs';
import { pushDangerNotification, pushSuccessfulNotification, pushWarningNotification } from '../notifications/actions';
import * as actionTypes from './actionTypes';
import { formValuesToCreatePayload, formValuesToGrantPayload, revokePayload } from './mapper';

const UNCONFIRMED_MESSAGE = 'The override was submitted but is taking longer than expected to apply. Refresh the list in a moment to confirm.';

/*
 * `services/jobs` knows what a `skipped` or `suppressed` job means, not what the job was doing, so
 * the noun is supplied here.
 */
const UNAPPLIED_MESSAGE = 'The override was not applied';

export const showComplianceVerdictFormModal = () => {
	return { type: actionTypes.SHOW_COMPLIANCE_VERDICT_FORM_MODAL };
};

export const hideComplianceVerdictFormModal = () => {
	return { type: actionTypes.HIDE_COMPLIANCE_VERDICT_FORM_MODAL };
};

export const showComplianceVerdictGrantModal = (verdict) => {
	return { type: actionTypes.SHOW_COMPLIANCE_VERDICT_GRANT_MODAL, payload: verdict };
};

export const hideComplianceVerdictGrantModal = () => {
	return { type: actionTypes.HIDE_COMPLIANCE_VERDICT_GRANT_MODAL };
};

export const showComplianceVerdictRevokeModal = (verdict) => {
	return { type: actionTypes.SHOW_COMPLIANCE_VERDICT_REVOKE_MODAL, payload: verdict };
};

export const hideComplianceVerdictRevokeModal = () => {
	return { type: actionTypes.HIDE_COMPLIANCE_VERDICT_REVOKE_MODAL };
};

export const getComplianceVerdicts = (queryParams = GET_COMPLIANCE_VERDICTS_DEFAULT_QUERY_PARAMS) => {
	return (dispatch) => {
		const serialNumber = String(queryParams?.serialNumber ?? '').trim();

		dispatch({ type: actionTypes.START_FETCHING_COMPLIANCE_VERDICTS, payload: { serialNumber: serialNumber } });

		return complianceVerdictsService
			.getComplianceVerdicts(queryParams)
			.then(({ data }) => {
				const verdicts = data?.complianceVerdicts ?? [];
				const pagination = data?.pagination ?? {};

				dispatch({
					type: actionTypes.GET_COMPLIANCE_VERDICTS_SUCCESS,
					payload: {
						verdicts: verdicts,
						pagination: pagination,
						/*
						 * The serial travels with the response so the slice records which filter the
						 * rows were answered for, rather than which one is currently committed.
						 */
						serialNumber: serialNumber
					}
				});

				return { verdicts: verdicts, pagination: pagination };
			})
			.catch((error) => {
				/*
				 * The service aborts the previous read on every new one, so a cancellation is the
				 * expected outcome of searching again quickly rather than a failure.
				 */
				if (error instanceof CanceledError) {
					return;
				}

				dispatch({
					type: actionTypes.GET_COMPLIANCE_VERDICTS_ERROR,
					payload: getApiErrorMessage(error, 'The compliance verdicts could not be loaded.')
				});
			});
	};
};

const isAbort = (error) => {
	return error instanceof CanceledError || error?.name === 'AbortError';
};

/*
 * How a run of the write pipeline ended, for a caller that has to decide whether the page may
 * follow the write. `failed` is the whole failing half of the branches documented on
 * `runOverrideWrite` below - a rejected request, a response carrying no job, and a job that ended
 * un-succeeded - because they differ only in what they say, and in none of them does the override
 * exist.
 */
export const OVERRIDE_WRITE_OUTCOMES = {
	APPLIED: 'applied',
	UNCONFIRMED: 'unconfirmed',
	FAILED: 'failed',
	ABANDONED: 'abandoned'
};

/*
 * Accepted is not the same as succeeded. An unconfirmed write was accepted by the API and only its
 * job could not be watched to the end, so the override it creates belongs to the serial that was
 * submitted just as an applied one does. A caller that moves the page to what was written has to
 * follow both, or an unconfirmed grant leaves the page on the appliance searched for beforehand.
 */
export const isOverrideWriteAccepted = (outcome) => {
	return outcome === OVERRIDE_WRITE_OUTCOMES.APPLIED || outcome === OVERRIDE_WRITE_OUTCOMES.UNCONFIRMED;
};

/*
 * Re-reads the page the write was made from, stepping back to the last page that still has rows
 * when the write emptied it. A grant under the New filter, or a revoke under Active, moves the row
 * out of the filter; when it was the only row on a later page, a plain re-read of the same offset
 * comes back empty - the API does not clamp `start` - and the table would claim there is nothing to
 * show while earlier pages still have rows.
 *
 * The page on screen follows the response's `pagination.currentPage`, so the component's own
 * `start` is left behind on the emptied offset. That is harmless: paging recomputes it from the
 * page clicked, the filters reset it, and the next write's refresh lands here again.
 */
const refreshAfterWrite = async (dispatch, queryParams) => {
	const page = await dispatch(getComplianceVerdicts(queryParams));
	const start = Number(queryParams?.start ?? 0);

	if (!page || page.verdicts.length > 0 || start <= 0) {
		return;
	}

	const limit = Number(queryParams?.limit) || page.pagination.itemsPerPage || GET_COMPLIANCE_VERDICTS_DEFAULT_QUERY_PARAMS.limit;
	const totalItems = page.pagination.totalItems ?? 0;
	const lastStart = totalItems > 0 ? Math.floor((totalItems - 1) / limit) * limit : 0;

	if (lastStart >= start) {
		return;
	}

	await dispatch(getComplianceVerdicts({ ...queryParams, start: lastStart }));
};

const abandonOverrideWrite = (dispatch) => {
	dispatch({ type: actionTypes.SAVE_COMPLIANCE_VERDICT_ABORTED });

	return OVERRIDE_WRITE_OUTCOMES.ABANDONED;
};

const failOverrideWrite = (dispatch, message) => {
	dispatch({ type: actionTypes.SAVE_COMPLIANCE_VERDICT_ERROR, payload: message });
	dispatch(pushDangerNotification(message));

	return OVERRIDE_WRITE_OUTCOMES.FAILED;
};

/*
 * The write pipeline every verdict change runs through: send, then poll the job it returns, then
 * refresh the list. Create, grant and revoke are the same five steps, so the steps live here rather
 * than in any one component.
 *
 * Three failure shapes, deliberately handled differently:
 *
 *   Request rejected      - nothing was accepted (a 422 from the schema, an expired session). The
 *                           modal stays open with the values intact so it can be corrected in place.
 *   Poll never concluded  - the write WAS accepted; all that failed is watching it finish. Saying
 *                           it failed would be wrong, so this closes the modal (a filled create
 *                           form left open invites a duplicate submit over a job still running),
 *                           refreshes the list, and says the outcome is unconfirmed.
 *   Job ended un-succeeded - pollJobUntilTerminal resolves on `failed`, `canceled`, `skipped` and
 *                           `suppressed` too. Unlike the advisory API, this is the PRIMARY error
 *                           path rather than an edge case: the endpoints validate only their
 *                           schemas synchronously, so an unknown component type id, an unknown
 *                           verdict id and a verdict gone stale since the list was read all come
 *                           back as a 200 plus a failed job. The modal stays open so a typed
 *                           reason survives. Grant and revoke also re-read the list first, because
 *                           their likeliest failure is a verdict that changed under the operator,
 *                           and a row left showing its stale action invites the same doomed retry.
 *                           Create does not: its `queryParams` name the serial it was writing to,
 *                           which the page only moves to once the write is accepted.
 *
 * Resolves rather than rejects on a handled failure: redux-form surfaces a rejection that is not a
 * SubmissionError as an unhandled rejection, and clears `submitting` either way. It resolves with
 * the `OVERRIDE_WRITE_OUTCOMES` member naming which of the four branches it took, so that a caller
 * whose own state depends on the write landing - the create form moves the page to the serial it
 * wrote - can commit that state on the accepted branches only, rather than up front where a
 * rejection would strand it.
 */
const runOverrideWrite = async (dispatch, sendRequest, options) => {
	const { successMessage, failureMessage, queryParams, signal, hideModal, refreshOnJobFailure } = options;
	const jobFailureMessage = options.jobFailureMessage ?? failureMessage;
	let job;
	let jobId;

	dispatch({ type: actionTypes.START_SAVING_COMPLIANCE_VERDICT });

	try {
		const { data } = await sendRequest();

		jobId = data?.jobId;
	} catch (error) {
		if (isAbort(error)) {
			return abandonOverrideWrite(dispatch);
		}

		return failOverrideWrite(dispatch, getApiErrorMessage(error, failureMessage));
	}

	if (!jobId) {
		return failOverrideWrite(dispatch, 'The request was accepted but returned no job to track.');
	}

	try {
		job = await pollJobUntilTerminal(jobId, { ...OVERRIDE_JOB_POLL, signal });
	} catch (error) {
		if (isAbort(error)) {
			return abandonOverrideWrite(dispatch);
		}

		dispatch({ type: actionTypes.SAVE_COMPLIANCE_VERDICT_ERROR, payload: UNCONFIRMED_MESSAGE });
		dispatch(pushWarningNotification(UNCONFIRMED_MESSAGE));
		dispatch(hideModal());
		await refreshAfterWrite(dispatch, queryParams);

		return OVERRIDE_WRITE_OUTCOMES.UNCONFIRMED;
	}

	if (!isJobSucceeded(job)) {
		/*
		 * Refreshed before the failure is reported so that `saving` stays raised - and the dialog's
		 * button dark - until the row behind it shows where the verdict actually stands.
		 */
		if (refreshOnJobFailure) {
			await refreshAfterWrite(dispatch, queryParams);
		}

		return failOverrideWrite(dispatch, describeJobOutcome(job, {
			failureMessage: jobFailureMessage,
			unappliedMessage: UNAPPLIED_MESSAGE
		}));
	}

	dispatch({ type: actionTypes.SAVE_COMPLIANCE_VERDICT_SUCCESS });
	dispatch(hideModal());
	dispatch(pushSuccessfulNotification(successMessage));

	/*
	 * The refresh is awaited inside the same promise rather than fired and forgotten, so the Save
	 * button stays disabled until the table actually shows the change.
	 */
	await refreshAfterWrite(dispatch, queryParams);

	return OVERRIDE_WRITE_OUTCOMES.APPLIED;
};

/*
 * `grantedBy`/`revokedBy` arrive as arguments rather than being read out of `getState`. Every thunk
 * in this codebase is a pure `(dispatch)` thunk, and reaching into the profile slice here would
 * force every action test to build a user tree to exercise an unrelated branch.
 */
export const createComplianceVerdict = (values, { grantedBy, queryParams, signal } = {}) => {
	return (dispatch) => {
		return runOverrideWrite(dispatch, () => {
			return complianceVerdictsService.createComplianceVerdict(formValuesToCreatePayload(values, grantedBy));
		}, {
			successMessage: 'Override created.',
			failureMessage: 'The override could not be created.',
			queryParams: queryParams,
			signal: signal,
			hideModal: hideComplianceVerdictFormModal
		});
	};
};

/*
 * The job fails, rather than the request, when the verdict is no longer current - a later passing
 * run cancelled it, or a later failure of the same component superseded it. The API's own message
 * is shown when the job carries one; the fallback names the likeliest cause, and says the list
 * behind the dialog has already been re-read to show it.
 */
export const grantComplianceVerdict = (id, values, { grantedBy, queryParams, signal } = {}) => {
	return (dispatch) => {
		return runOverrideWrite(dispatch, () => {
			return complianceVerdictsService.grantComplianceVerdict(id, formValuesToGrantPayload(values, grantedBy));
		}, {
			successMessage: 'Override granted.',
			failureMessage: 'The override could not be granted.',
			jobFailureMessage: 'The override could not be granted. The verdict may no longer be awaiting a decision - the list has been refreshed to show its current status.',
			queryParams: queryParams,
			signal: signal,
			hideModal: hideComplianceVerdictGrantModal,
			refreshOnJobFailure: true
		});
	};
};

export const revokeComplianceVerdict = (id, { revokedBy, queryParams, signal } = {}) => {
	return (dispatch) => {
		return runOverrideWrite(dispatch, () => {
			return complianceVerdictsService.revokeComplianceVerdict(id, revokePayload(revokedBy));
		}, {
			successMessage: 'Override revoked.',
			failureMessage: 'The override could not be revoked.',
			jobFailureMessage: 'The override could not be revoked. The list has been refreshed to show its current status.',
			queryParams: queryParams,
			signal: signal,
			hideModal: hideComplianceVerdictRevokeModal,
			refreshOnJobFailure: true
		});
	};
};
